import React from 'react';
import { Feature } from '@tencent/workbench';
import type { NumberInputFrom } from '@tencent/wb-ext-form';
import BaseDrawerEffectFeature from '@framework/workbench/common/feature/base-drawer-effect-feature';
import { EffectPropsField } from '@editor/src/js/constants';
import ReflectionPresetMenu from '@framework/frame-pc/components/palette-panel/effect-preset-menu/reflection-preset-menu/index-text';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import PresetPanel from '@framework/workbench/drawer/components/preset-panel';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { IReflectionModelVal } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import _isEqualWith from 'lodash/isEqualWith';
import { MULTI } from '@src/js/constant';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';

const defaultReflectionProps: IReflectionModelVal = {
    startOpacity: 0,
    endOpacity: 99.7,
    size: 0,
    blurRad: 0,
    dist: 0,
    sy: -1,
    dir: 90,
};
const REFLECTION_EMPTY = 'reflect-empty';

@Feature()
export class ReflectionFeature extends BaseDrawerEffectFeature {
    public id = ActionType.FONT_EFFECT;

    // 侧边栏展示数据
    public reflectionInfoInDrawer: IReflectionModelVal = { ...defaultReflectionProps };

    // 预览时的临时数据
    private reflectionInfoInPreview?: IReflectionModelVal;

    // 获取uiModel上的数据
    public get reflectionInfoInModel(): IReflectionModelVal {
        const data = this.getFontEffect(ActionType.REFLECTION);
        return (data === MULTI ? null : data) ?? defaultReflectionProps;
    }

    // 是否是部分设置了效果或者不同部分设置了不同的效果
    public get isMultiDifferent(): boolean {
        return this.isTextMultiDiffValue(ActionType.REFLECTION);
    }

    // 获取侧边栏大小数据（预览态有值时说明处于预览态，优先预览态数据）
    public get size(): number {
        return (this.reflectionInfoInPreview || this.reflectionInfoInDrawer).size;
    }

    // 获取侧边栏模糊数据（预览态有值时说明处于预览态，优先预览态数据）
    public get blur(): number {
        return (this.reflectionInfoInPreview || this.reflectionInfoInDrawer).blurRad;
    }

    // 获取侧边栏距离数据（预览态有值时说明处于预览态，优先预览态数据）
    public get dist(): number {
        return (this.reflectionInfoInPreview || this.reflectionInfoInDrawer).dist;
    }

    // 获取侧边栏透明度数据（预览态有值时说明处于预览态，优先预览态数据）
    public get opacity(): number {
        return Math.round((this.reflectionInfoInPreview
            || this.reflectionInfoInDrawer)[EffectPropsField.STARTOPACITY.name]) || 0;
    }

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.TEXT_REFLECTION);
    }

    public initPropsCallback = (): void => {
        // 点击文字时，侧边栏同步大小透明度等数据
        if (!this.getTextEditorStateService().isTextPreview) {
            this.reflectionInfoInPreview = undefined;
            this.reflectionInfoInDrawer = { ...this.reflectionInfoInModel };
        }
    };

    // 类型切换面板
    public renderPreset(): any {
        // 表格切换单元格的数值更新，可以靠slide-adaptor触发的onSelectionChange联动触发
        this.initPropsCallback();
        const { iconSrc, iconName } = this.getPresetStyle(this.reflectionInfoInModel);
        // 当前预览icon
        return  (<PresetPanel
            src={iconSrc}
            hasEffect={iconName !== REFLECTION_EMPTY}
            isMultiDifferent={this.isMultiDifferent}
            onVisibleChange={this.handlePresetVisibleChange}
            disabled={this.isMultiModel() && this.isTable()}
        >
            {
                this.getPresetMenuContent()
            }
        </PresetPanel>);
    }

    public getPresetMenuContent = (props?: { style: React.CSSProperties }) => {
        const { iconName } = this.getPresetStyle(this.reflectionInfoInModel);
        return  (
            <ReflectionPresetMenu
                containerStyle={props?.style}
                preset={{ style: iconName }}
                onReflectionPresetStyleClick={reflection => this.handleReflectionPresetStyleClick(reflection)}
                onReflectionPresetStyleMouseEnter={
                    reflection => this.handleReflectionPresetStyleEnter(reflection)
                }
                onReflectionPresetStyleMouseLeave={() => this.handleReflectionPresetStyleMouseLeave()} />);
    };

    /**
     * 各项指标编辑
     * @param value 指标值
     * @param isPreview 是否是预览（比如拖拽过程中）
     * @param from 编辑来源
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TEXT_REFLECTION)
    public handleReflectionChange(
        value: Partial<IReflectionModelVal>,
        dragEnd?: boolean,
        from?: NumberInputFrom,
    ): void {
        const isPreview = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (isPreview) {
            // 拖拽滑动过程中预览
            this.reflectionInfoInPreview = { ...this.reflectionInfoInModel, ...value };
            // 因为文字效果实时更新太卡，所以拖拽过程中不渲染效果
            return;
        }
        // 点击、输入、滑动停止
        this.reflectionInfoInDrawer = { ...this.reflectionInfoInModel, ...value };

        // 更新当前预览状态
        this.updatePreviewState(isPreview);
        // 更新文字ui，非预览态则还要上传sdk
        this.updateUiAndSdk();
        this.handleReflectionReport(value, from, isPreview);
    }

    private updateUiAndSdk = () => {
        const data = this.getTextEditorStateService().isTextPreview
            ? { ...this.reflectionInfoInPreview } : { ...this.reflectionInfoInDrawer };
        const values = { [ActionType.EFFECT]: {} };
        if (data && !_isEqualWith(data, defaultReflectionProps)) {
            values[ActionType.EFFECT][ActionType.REFLECTION] = data;
        } else {
            values[ActionType.EFFECT][ActionType.REFLECTION] = null;
        }
        this.updateProps(this.id, values);
    };

    /**
     * 从‘预设’面板切换不同的阴影类型
     * @param {object} reflection 属性
     */
    private handleReflectionPresetStyleClick = (reflection: IReflectionModelVal) => {
        this.updatePreviewState(false);
        this.reflectionInfoInDrawer = { ...defaultReflectionProps, ...reflection };
        this.updateUiAndSdk();
        this.tdwDrawerReport(FormItemName.FONT_REFLECTION, { ver5: reflection ? 2 : 1 });
    };

    private handleReflectionPresetStyleEnter = (reflection: IReflectionModelVal) => {
        this.updatePreviewState(true);
        this.reflectionInfoInPreview = { ...defaultReflectionProps, ...reflection };
        this.updateUiAndSdk();
    };

    private handleReflectionPresetStyleMouseLeave = () => {
        this.reflectionInfoInPreview = { ...this.reflectionInfoInDrawer };
        if (this.getTextEditorStateService().isTextPreview) {
            this.callbackByActionType(ActionType.FONT_REFRESH);
            this.updatePreviewState(false);
        }
    };

    private handlePresetVisibleChange = (visible: boolean) => {
        if (visible) {
            this.reflectionInfoInDrawer = { ...this.reflectionInfoInModel };
        }
    };

    // 更新全局文本预览状态（如果是退出预览态则同时清空预览数据）
    private updatePreviewState = (isPreview: boolean) => {
        this.getTextEditorStateService().isTextPreview = isPreview;
        if (!isPreview) {
            this.reflectionInfoInPreview = undefined;
        }
    };

    private handleReflectionReport = (value, from?: NumberInputFrom, onlyChangeUI?: boolean) => {
        let reportId;
        let ver6;
        if (value?.size) {
            reportId  = FormItemName.FONT_REFLECTION_SIZE;
            ver6 = value.size;
        }
        if (value?.blurRad) {
            reportId  = FormItemName.FONT_REFLECTION_BLUR;
            ver6 = value.blurRad;
        }
        if (value?.dist) {
            reportId  = FormItemName.FONT_REFLECTION_DISTANCE;
            ver6 = value.dist;
        }
        if (value?.[EffectPropsField.STARTOPACITY.name]) {
            reportId  = FormItemName.FONT_REFLECTION_OPACITY;
            ver6 = value?.[EffectPropsField.STARTOPACITY.name];
        }
        this.tdwDrawerReportNumberInput(reportId, { ver6 }, from, onlyChangeUI);
    };

    private getPresetStyle = (reflectionProps: IReflectionModelVal) => {
        const { dist = 0, size = 0 } = reflectionProps;
        let iconSrc = '';
        let iconName = '';
        if (dist === 0 && size === 0) {
            iconName = 'reflect-empty';
        } else if (dist === 0) {
            iconName = 'close-0-dy';
        } else if (dist < 3) {
            iconName = 'close-4-dy';
        } else {
            iconName = 'close-8-dy';
        }
        iconSrc = ReflectionPresetMenu.PresetIconOnShow[iconName];
        return {
            iconSrc, iconName,
        };
    };
}
